import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { usePageReady } from '../../context/PageReadyContext';
import { getPageLoadConfig } from '../../utils/pageCriticalImages';
import '../../styles/layout/RouteTransition.css';

const REVEAL_FADE_MS = 480;

function RouteTransition() {
  const location = useLocation();
  const { isPageReady } = usePageReady();
  const isFirstLoad = useRef(true);
  const transitionStartRef = useRef(Date.now());
  const timingRef = useRef(getPageLoadConfig('/'));
  const revealTimerRef = useRef(null);
  const maxTimerRef = useRef(null);
  const hideTimerRef = useRef(null);
  const [phase, setPhase] = useState('cover');
  const [animationKey, setAnimationKey] = useState(0);
  const [variant, setVariant] = useState('nav');

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname, location.search, location.hash]);

  useLayoutEffect(() => {
    const isHome = location.pathname === '/';
    const useFullIntro = isFirstLoad.current && isHome;
    isFirstLoad.current = false;

    const timing = getPageLoadConfig(location.pathname);
    timingRef.current = timing;
    transitionStartRef.current = Date.now();
    setVariant(useFullIntro ? 'intro' : 'nav');
    setPhase('cover');
    setAnimationKey((prev) => prev + 1);

    const showAnimationTimer = window.setTimeout(() => {
      setPhase('animate');
    }, useFullIntro ? 220 : 70);

    return () => {
      window.clearTimeout(showAnimationTimer);
      window.clearTimeout(revealTimerRef.current);
      window.clearTimeout(maxTimerRef.current);
      window.clearTimeout(hideTimerRef.current);
    };
  }, [location.pathname]);

  useLayoutEffect(() => {
    if (phase === 'hidden' || phase === 'revealing') {
      return undefined;
    }

    const timing = timingRef.current;
    const elapsed = Date.now() - transitionStartRef.current;
    const remainingMin = Math.max(0, timing.minDuration - elapsed);
    const remainingMax = timing.maxDuration == null
      ? null
      : Math.max(0, timing.maxDuration - elapsed);

    window.clearTimeout(revealTimerRef.current);
    window.clearTimeout(maxTimerRef.current);

    const beginReveal = () => {
      setPhase('revealing');
      hideTimerRef.current = window.setTimeout(() => {
        setPhase('hidden');
      }, REVEAL_FADE_MS);
    };

    if (isPageReady) {
      revealTimerRef.current = window.setTimeout(beginReveal, remainingMin);
    }

    if (remainingMax != null) {
      maxTimerRef.current = window.setTimeout(beginReveal, remainingMax);
    }

    return () => {
      window.clearTimeout(revealTimerRef.current);
      window.clearTimeout(maxTimerRef.current);
    };
  }, [phase, isPageReady, location.pathname]);

  useEffect(() => {
    if (phase !== 'animate' || isPageReady) {
      return undefined;
    }

    const loopTimer = window.setInterval(() => {
      setAnimationKey((prev) => prev + 1);
    }, 1300);

    return () => {
      window.clearInterval(loopTimer);
    };
  }, [phase, isPageReady]);

  useLayoutEffect(() => {
    const isLoading = phase === 'cover' || phase === 'animate';
    document.body.classList.toggle('route-is-loading', isLoading);
    document.body.classList.toggle('route-is-revealing', phase === 'revealing');

    return () => {
      document.body.classList.remove('route-is-loading');
      document.body.classList.remove('route-is-revealing');
    };
  }, [phase]);

  return (
    <div
      className={`route-loader route-loader--${variant} phase-${phase}`}
      aria-hidden={phase === 'hidden'}
      aria-busy={phase !== 'hidden'}
    >
      <div className={`route-loader-window ${phase === 'animate' ? 'show' : ''}`}>
        {(phase === 'cover' || phase === 'animate') && (
          <div className="route-loader-window-frame" key={animationKey}>
            <div className="route-loader-top-rail"></div>
            <div className="route-loader-blind"></div>
            <div className="route-loader-cord">
              <span className="route-loader-cord-line"></span>
              <span className="route-loader-cord-knob"></span>
            </div>
            <div className="route-loader-divider"></div>
          </div>
        )}
      </div>
    </div>
  );
}

export default RouteTransition;
